"use client";

import { useFeatureFlagEnabled } from "posthog-js/react";
import { APPLY_MOMENT_SIMPLIFIED_FLAG } from "@/lib/jobs/apply-moment";
import { lowFitNotice } from "@/lib/match/low-fit";
import { useJobContext } from "./job-context";

type Props = { locale: string };

/**
 * Factual low-fit line rendered beside the apply CTA on the job page. Reads
 * the personalised fit from the JobContext island (same source as
 * `FitScoreBadge`), so it paints only for signed-in users whose score is
 * `low`; guests and medium/high fits render nothing. Static text, no
 * interaction, tokens only (dark-mode safe).
 */
export function LowFitNotice({ locale }: Props) {
	const { fitScoreBreakdown } = useJobContext();
	// `apply-moment-simplified` folds this line into the CTA's single muted note.
	const simplified =
		useFeatureFlagEnabled(APPLY_MOMENT_SIMPLIFIED_FLAG) === true;
	const notice = lowFitNotice(locale, fitScoreBreakdown);
	if (!notice || simplified) return null;
	return (
		<p
			data-testid="low-fit-notice"
			className="rounded-md border border-border/60 bg-muted/40 px-3 py-2 text-xs text-muted-foreground"
		>
			<span className="font-semibold text-foreground">{notice.headline}</span>
			{" — "}
			{notice.detail}
		</p>
	);
}
